import { useState } from "react";
import Toast from "@/components/base/Toast";
import { useToast } from "@/hooks/useToast";
import { calendarWeek } from "@/mocks/home";

export default function CheckinCalendar() {
  const { message, show } = useToast();
  const [days, setDays] = useState(calendarWeek);

  const checkedCount = days.filter((d) => d.done).length;
  const today = days.find((d) => d.today);

  const handleCheckin = (id: string, done: boolean) => {
    if (done) {
      show("今天已经打过卡啦");
      return;
    }
    setDays((prev) => prev.map((d) => (d.id === id ? { ...d, done: true } : d)));
    show("打卡成功，连续天数 +1");
  };

  return (
    <section id="calendar" className="border-b border-background-200 bg-background-100/60">
      <div className="mx-auto w-full max-w-6xl px-4 py-14 md:px-8 md:py-20">
        <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div>
            <span className="font-label text-xs font-medium uppercase tracking-[0.2em] text-primary-600">
              Follow Calendar
            </span>
            <h2 className="mt-3 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
              追更日历 · 打卡订阅
            </h2>
            <p className="mt-3 max-w-2xl text-sm leading-7 text-foreground-600">
              这一周每天的更新量都标在格子里，方便提前规划蹲守节奏。看完当天的内容就顺手打个卡，
              连续天数会显示在你的评论昵称旁。
            </p>
          </div>
          <button
            type="button"
            onClick={() => handleCheckin(today ? today.id : "d-6", today ? today.done : false)}
            className="inline-flex w-fit cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-secondary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-secondary-600 dark:text-foreground-950"
          >
            <span className="flex h-4 w-4 items-center justify-center">
              <i className="ri-calendar-check-line"></i>
            </span>
            今日打卡
          </button>
        </div>

        <div className="mt-9 grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-7">
          {days.map((day) => (
            <button
              key={day.id}
              type="button"
              onClick={() => handleCheckin(day.id, day.done)}
              className={`cursor-pointer rounded-lg border p-4 text-left transition-colors ${
                day.today
                  ? "border-primary-400 bg-primary-50"
                  : day.done
                    ? "border-secondary-200 bg-secondary-50"
                    : "border-background-200 bg-background-50 hover:border-background-300"
              }`}
            >
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-foreground-700">{day.label}</span>
                {day.done ? (
                  <span className="flex h-4 w-4 items-center justify-center text-secondary-600">
                    <i className="ri-checkbox-circle-fill"></i>
                  </span>
                ) : (
                  <span className="h-2 w-2 rounded-full bg-background-300" />
                )}
              </div>
              <div className="mt-3 font-heading text-lg font-bold tabular-nums text-foreground-950">
                {day.date}
              </div>
              <div className="mt-1 text-[11px] text-foreground-500">
                {day.count > 0 ? `${day.count} 条更新` : "无更新安排"}
              </div>
              {day.today && (
                <div className="mt-2 inline-block rounded-sm bg-primary-500 px-1.5 py-0.5 text-[10px] text-background-50 dark:text-foreground-950">
                  今天
                </div>
              )}
            </button>
          ))}
        </div>

        <div className="mt-8 grid grid-cols-1 gap-4 md:grid-cols-3">
          <div className="rounded-lg border border-background-200 bg-background-50 p-5">
            <div className="flex items-center gap-2 text-xs text-foreground-500">
              <span className="flex h-4 w-4 items-center justify-center">
                <i className="ri-calendar-line"></i>
              </span>
              本周打卡
            </div>
            <div className="mt-2 font-heading text-3xl font-bold text-foreground-950">
              {checkedCount}
              <span className="ml-1 text-base font-medium text-foreground-400">/ 7 天</span>
            </div>
          </div>
          <div className="rounded-lg border border-background-200 bg-background-50 p-5">
            <div className="flex items-center gap-2 text-xs text-foreground-500">
              <span className="flex h-4 w-4 items-center justify-center">
                <i className="ri-fire-line text-primary-500"></i>
              </span>
              连续打卡
            </div>
            <div className="mt-2 font-heading text-3xl font-bold text-primary-600">
              42
              <span className="ml-1 text-base font-medium text-foreground-400">天</span>
            </div>
          </div>
          <div className="rounded-lg border border-background-200 bg-background-50 p-5">
            <div className="flex items-center gap-2 text-xs text-foreground-500">
              <span className="flex h-4 w-4 items-center justify-center">
                <i className="ri-medal-line text-accent-600"></i>
              </span>
              本周追更进度
            </div>
            <div className="mt-2 font-heading text-3xl font-bold text-foreground-950">
              86<span className="ml-1 text-base font-medium text-foreground-400">%</span>
            </div>
            <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-background-200">
              <div className="h-full w-[86%] rounded-full bg-accent-500" />
            </div>
          </div>
        </div>
      </div>
      <Toast message={message} />
    </section>
  );
}